import { type CSSProperties, type ElementType, type ReactNode } from "react";
import { useScrollEffect } from "./useScrollEffect";

type ParallaxProps = {
  children: ReactNode;
  /** Positive moves slower (drifts up as you scroll); range ~0.1–0.4 feels good. */
  speed?: number;
  className?: string;
  as?: ElementType;
  style?: CSSProperties;
};

/**
 * Vertical parallax wrapper. The inner element drifts against scroll while its
 * container is in view. No-op for reduced-motion / no-JS (content renders
 * normally). Pair with `overflow: hidden` on the container for image layers.
 */
export function Parallax({
  children,
  speed = 0.2,
  className,
  as: Tag = "div",
  style,
}: ParallaxProps) {
  const ref = useScrollEffect<HTMLDivElement>(
    ({ gsap }, scope) => {
      const shift = scope.offsetHeight * speed;
      gsap.fromTo(
        scope,
        { yPercent: -speed * 50 },
        {
          yPercent: speed * 50,
          ease: "none",
          scrollTrigger: {
            trigger: scope.parentElement ?? scope,
            start: "top bottom",
            end: "bottom top",
            scrub: true,
          },
        },
      );
      return () => void shift;
    },
    [speed],
  );

  const Component = Tag as ElementType;
  return (
    <Component ref={ref} className={className} style={style}>
      {children}
    </Component>
  );
}
